import React, { useState, useEffect } from 'react';
import { X, DollarSign, AlertCircle, Loader2 } from 'lucide-react';
import { slotApi } from '../services/api';
import type { BulkSlotCreate } from '../types';
import { extractApiError } from '../utils/errorUtils';
import { showSuccess, showError } from '../utils/toast';

/**
 * Bulk Slot Create Modal Props
 */
interface BulkSlotCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  fairId: number;
  onSuccess: () => void;
}

/**
 * Bulk Slot Create Modal Component
 *
 * @component
 * @description Modal form for bulk creating booth slots within a fair. Used in Fair Manager Slot Management interface.
 *
 * ## E2E Testing
 *
 * ### Required Fields (form won't submit without these):
 * - `slotPrefix` (`#slotPrefix`) - Prefix for slot numbers (e.g., "A", "Booth")
 * - `startNumber` (`#startNumber`) - Starting slot number (e.g., 1)
 * - `endNumber` (`#endNumber`) - Ending slot number (e.g., 50)
 * - `basePrice` (`#basePrice`) - Daily rental price, min 0, step 0.01
 *
 * ### Optional Fields:
 * - `dimensions` (`#dimensions`) - Physical size, max 100 chars (e.g., "10x10 feet")
 * - `hasPower` (`#hasPower`) - Checkbox for electrical power access
 * - `slotType` (`#slotType`) - Slot category (common, preferred, premium, vip)
 *
 * ### Validation Rules:
 * - Slot prefix: Required, max 20 chars
 * - Start number: Required, must be positive integer
 * - End number: Required, must be >= start number
 * - Base price: Required, must be valid positive number
 *
 * @example
 * // E2E Test Example - Bulk Create Slots
 * await page.click('[data-testid="bulk-create-slots-button"]'); // Open modal
 * await page.fill('#slotPrefix', 'A');
 * await page.fill('#startNumber', '1');
 * await page.fill('#endNumber', '50');
 * await page.fill('#basePrice', '100.00');
 * await page.fill('#dimensions', '10x10 feet');
 * await page.selectOption('#slotType', 'common');
 * await page.click('button[type="submit"]:has-text("Create Slots")');
 * await page.waitForSelector('.success'); // Success message
 *
 * @see {@link Issue #1193} - Fair Manager slot management missing UI buttons
 */

const SLOT_TYPES = [
  { value: 'common', label: 'Common' },
  { value: 'preferred', label: 'Preferred' },
  { value: 'premium', label: 'Premium' },
  { value: 'vip', label: 'VIP' },
];

// Maximum number of slots that can be created in a single bulk operation
// to prevent performance issues and potential DoS scenarios
const MAX_BULK_SLOTS = 500;

// Threshold for showing confirmation dialog (warn user for large bulk operations)
const LARGE_BULK_THRESHOLD = 50;

const BulkSlotCreateModal: React.FC<BulkSlotCreateModalProps> = ({
  isOpen,
  onClose,
  fairId,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Form state
  const [slotPrefix, setSlotPrefix] = useState('');
  const [startNumber, setStartNumber] = useState('');
  const [endNumber, setEndNumber] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [hasPower, setHasPower] = useState(false);
  const [slotType, setSlotType] = useState('common');
  const [basePrice, setBasePrice] = useState('');

  // Validation errors
  const [slotPrefixError, setSlotPrefixError] = useState('');
  const [startNumberError, setStartNumberError] = useState('');
  const [endNumberError, setEndNumberError] = useState('');
  const [basePriceError, setBasePriceError] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    // Reset form
    setSlotPrefix('');
    setStartNumber('');
    setEndNumber('');
    setDimensions('');
    setHasPower(false);
    setSlotType('common');
    setBasePrice('');
    setError(null);
    setSlotPrefixError('');
    setStartNumberError('');
    setEndNumberError('');
    setBasePriceError('');
    setLoading(false);
    setShowConfirmDialog(false);
  }, [isOpen]);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  const validateForm = (): boolean => {
    let isValid = true;

    // Validate slot prefix
    if (!slotPrefix.trim()) {
      setSlotPrefixError('Slot prefix is required');
      isValid = false;
    } else if (slotPrefix.length > 20) {
      setSlotPrefixError('Slot prefix must be 20 characters or less');
      isValid = false;
    } else {
      setSlotPrefixError('');
    }

    // Validate start number
    const startNum = parseInt(startNumber);
    if (!startNumber || isNaN(startNum) || startNum < 1) {
      setStartNumberError('Start number must be a positive integer');
      isValid = false;
    } else {
      setStartNumberError('');
    }

    // Validate end number
    const endNum = parseInt(endNumber);
    if (!endNumber || isNaN(endNum) || endNum < 1) {
      setEndNumberError('End number must be a positive integer');
      isValid = false;
    } else if (endNum < startNum) {
      setEndNumberError('End number must be greater than or equal to start number');
      isValid = false;
    } else {
      // Check if the total number of slots exceeds the maximum limit
      const slotsToCreate = endNum - startNum + 1;
      if (slotsToCreate > MAX_BULK_SLOTS) {
        setEndNumberError(`Cannot create more than ${MAX_BULK_SLOTS} slots at once. Please use smaller ranges.`);
        isValid = false;
      } else {
        setEndNumberError('');
      }
    }

    // Validate base price
    if (!basePrice.trim()) {
      setBasePriceError('Base price is required');
      isValid = false;
    } else {
      const price = parseFloat(basePrice);
      if (isNaN(price) || price < 0) {
        setBasePriceError('Base price must be a valid positive number');
        isValid = false;
      } else {
        setBasePriceError('');
      }
    }

    return isValid;
  };

  const performBulkCreate = async () => {
    const bulkData: BulkSlotCreate = {
      fair_id: fairId,
      slot_prefix: slotPrefix.trim(),
      start_number: parseInt(startNumber),
      end_number: parseInt(endNumber),
      base_price: basePrice,
      slot_type: slotType,
      dimensions: dimensions.trim() || undefined,
      has_power: hasPower,
    };

    try {
      setLoading(true);
      setError(null);
      await slotApi.createSlotsBulk(fairId, bulkData);
      const slotsCreated = bulkData.end_number - bulkData.start_number + 1;
      showSuccess(`Successfully created ${slotsCreated} slots`);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to bulk create slots:', apiError);
      setError(apiError.message);
      showError('Failed to create slots', {
        description: apiError.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Calculate slots to create
    const slotsToCreate = parseInt(endNumber) - parseInt(startNumber) + 1;

    // Show confirmation dialog for large bulk operations
    if (slotsToCreate > LARGE_BULK_THRESHOLD && !showConfirmDialog) {
      setShowConfirmDialog(true);
      return;
    }

    await performBulkCreate();
  };

  if (!isOpen) return null;

  // Calculate slots to create with proper NaN handling
  const slotsToCreate = startNumber && endNumber &&
    !isNaN(parseInt(startNumber)) && !isNaN(parseInt(endNumber))
    ? Math.max(0, parseInt(endNumber) - parseInt(startNumber) + 1)
    : 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      data-testid="bulk-slot-create-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900" data-testid="bulk-slot-modal-heading">
            Bulk Create Slots
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close modal"
            data-testid="bulk-slot-modal-close-button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2" data-testid="bulk-slot-error">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Slot Prefix */}
          <div>
            <label htmlFor="slotPrefix" className="block text-sm font-medium text-gray-700 mb-1">
              Slot Prefix <span className="text-red-500">*</span>
            </label>
            <input
              id="slotPrefix"
              type="text"
              value={slotPrefix}
              onChange={(e) => {
                setSlotPrefix(e.target.value);
                setSlotPrefixError('');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                slotPrefixError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., A, Booth, etc."
              disabled={loading}
              maxLength={20}
            />
            {slotPrefixError && (
              <p className="mt-1 text-sm text-red-600">{slotPrefixError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Prefix for slot numbers (e.g., "A" creates A1, A2, A3...)
            </p>
          </div>

          {/* Start Number */}
          <div>
            <label htmlFor="startNumber" className="block text-sm font-medium text-gray-700 mb-1">
              Start Number <span className="text-red-500">*</span>
            </label>
            <input
              id="startNumber"
              type="number"
              value={startNumber}
              onChange={(e) => {
                setStartNumber(e.target.value);
                setStartNumberError('');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                startNumberError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="1"
              disabled={loading}
              min="1"
              step="1"
            />
            {startNumberError && (
              <p className="mt-1 text-sm text-red-600">{startNumberError}</p>
            )}
          </div>

          {/* End Number */}
          <div>
            <label htmlFor="endNumber" className="block text-sm font-medium text-gray-700 mb-1">
              End Number <span className="text-red-500">*</span>
            </label>
            <input
              id="endNumber"
              type="number"
              value={endNumber}
              onChange={(e) => {
                setEndNumber(e.target.value);
                setEndNumberError('');
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                endNumberError ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="50"
              disabled={loading}
              min="1"
              step="1"
            />
            {endNumberError && (
              <p className="mt-1 text-sm text-red-600">{endNumberError}</p>
            )}
            {slotsToCreate > 0 && !endNumberError && (
              <p className="mt-1 text-sm text-secondary" data-testid="slots-to-create-count">
                Will create {slotsToCreate} slot{slotsToCreate !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Base Price */}
          <div>
            <label htmlFor="basePrice" className="block text-sm font-medium text-gray-700 mb-1">
              Base Price (per day) <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="h-5 w-5 text-gray-400" />
              </div>
              <input
                id="basePrice"
                type="text"
                value={basePrice}
                onChange={(e) => {
                  setBasePrice(e.target.value);
                  setBasePriceError('');
                }}
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  basePriceError ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="100.00"
                disabled={loading}
              />
            </div>
            {basePriceError && (
              <p className="mt-1 text-sm text-red-600">{basePriceError}</p>
            )}
          </div>

          {/* Dimensions (Optional) */}
          <div>
            <label htmlFor="dimensions" className="block text-sm font-medium text-gray-700 mb-1">
              Dimensions (Optional)
            </label>
            <input
              id="dimensions"
              type="text"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="e.g., 10x10 feet"
              disabled={loading}
              maxLength={100}
            />
          </div>

          {/* Slot Type */}
          <div>
            <label htmlFor="slotType" className="block text-sm font-medium text-gray-700 mb-1">
              Slot Type
            </label>
            <select
              id="slotType"
              value={slotType}
              onChange={(e) => setSlotType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
            >
              {SLOT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Has Power */}
          <div className="flex items-center gap-2">
            <input
              id="hasPower"
              type="checkbox"
              checked={hasPower}
              onChange={(e) => setHasPower(e.target.checked)}
              className="h-4 w-4 text-secondary focus:ring-primary border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="hasPower" className="text-sm text-gray-700">
              Has electrical power
            </label>
          </div>

          {/* Confirmation Dialog for Large Bulk Operations */}
          {showConfirmDialog && slotsToCreate > LARGE_BULK_THRESHOLD && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-testid="bulk-slot-confirm-dialog">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-yellow-900 mb-1">Large Bulk Operation</h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    You're about to create <strong>{slotsToCreate} slots</strong>. This operation may take a few moments to complete. Are you sure you want to proceed?
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowConfirmDialog(false)}
                      className="px-3 py-1.5 text-sm border border-yellow-300 rounded hover:bg-yellow-100 text-yellow-900"
                      data-testid="bulk-slot-confirm-cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowConfirmDialog(false);
                        // Proceed with bulk creation
                        performBulkCreate();
                      }}
                      className="px-3 py-1.5 text-sm bg-yellow-600 text-white rounded hover:bg-yellow-700"
                      data-testid="bulk-slot-confirm-proceed"
                    >
                      Yes, Create {slotsToCreate} Slots
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer Buttons */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
              disabled={loading}
              data-testid="bulk-slot-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              disabled={loading}
              data-testid="bulk-slot-submit-button"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? 'Creating...' : 'Create Slots'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkSlotCreateModal;
